import Decimal from 'decimal.js';

import type {
  HoldingIssue,
  HoldingStatus,
  ImportHolding,
  ImportWarning,
} from 'pages/assistant/services/portfolioDocumentImport';
import type { ManualPortfolioRow } from '../manualPortfolioPayload';

// Pure model of the confirmation table: editable rows from the endpoint's
// holdings, what blocks «Usa questi dati», and the hand-over to the manual
// paste table (whose submit builds the one payload the agent receives).

/** The figure the analysis runs on — the manual table's Peso / Controvalore toggle. */
export type FigureMode = 'weights' | 'values';

/** Figures are dot-decimal numeric strings ('' = missing), as react-number-format reports them. */
export type ImportRow = {
  rowId: string;
  isin: string;
  description: string | null;
  currency: string | null;
  quantity: string;
  value: string;
  weight: string;
  inCatalog: boolean;
  status: HoldingStatus;
  issues: HoldingIssue[];
};

/** The short tag under the ISIN (about 12 characters); OK rows show none, the issues go in its tooltip. */
export const STATUS_LABELS: Record<HoldingStatus, string> = {
  ok: 'OK',
  check_figures: 'Da verificare',
  isin_repaired: 'ISIN corretto',
  out_of_catalog: 'Fuori catalogo',
};

export const ISSUE_LABELS: Record<HoldingIssue, string> = {
  missing_value: 'controvalore non trovato',
  missing_weight: 'peso non trovato',
  missing_quantity: 'quantità non trovata',
  arithmetic_mismatch: 'le cifre della riga non sono coerenti tra loro',
  isin_ocr_repaired: 'ISIN corretto da una lettura errata: verificalo',
  not_in_catalog: 'non presente nel catalogo Fineco',
  mixed_currency_no_weight: 'valute diverse: inserisci il peso',
  missing_currency: 'valuta non indicata',
};

export const WARNING_LABELS: Record<ImportWarning, string> = {
  multiple_portfolios: 'Il documento sembra contenere più portafogli: indica le pagine di quello da analizzare.',
  over_page_cap: 'Il documento ha più di 10 pagine: indica le pagine con le posizioni.',
  low_image_quality: 'Immagine di bassa qualità: controlla con attenzione ISIN e importi.',
  no_isin_found: 'Nessun ISIN trovato nel documento.',
};

export const MAX_PAGES = 10;
export const MAX_PORTFOLIO_DOC_BYTES = 20 * 1024 * 1024;
export const PORTFOLIO_DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.csv';

const ISIN_PATTERN = /^[A-Z]{2}[A-Z0-9]{9}\d$/;

const toDraft = (value: number | null): string => (value === null || value === undefined ? '' : String(value));

const toNumber = (draft: string): number | null => {
  if (draft.trim() === '') return null;
  const parsed = Number(draft);
  return Number.isFinite(parsed) ? parsed : null;
};

export function toImportRows(holdings: ImportHolding[]): ImportRow[] {
  return holdings.map((holding) => ({
    rowId: holding.row_id,
    isin: holding.isin,
    description: holding.description,
    currency: holding.currency,
    quantity: toDraft(holding.quantity),
    value: toDraft(holding.value),
    weight: toDraft(holding.weight),
    inCatalog: holding.in_catalog && holding.status !== 'out_of_catalog',
    status: holding.status,
    issues: holding.issues,
  }));
}

/** Rows that reach the agent: out_of_catalog rows are shown, never sent. */
export const sendableRows = (rows: ImportRow[]): ImportRow[] => rows.filter((row) => row.inCatalog);

/** No FX: values in different currencies cannot be added up, so only weights work. */
export function valuesModeAllowed(rows: ImportRow[]): boolean {
  const currencies = new Set(
    sendableRows(rows)
      .map((row) => row.currency)
      .filter(Boolean),
  );
  return currencies.size <= 1;
}

/** Values when every sendable row has one (a partial weights list must total 100, values need not), else weights. */
export function defaultFigureMode(rows: ImportRow[]): FigureMode {
  const sendable = sendableRows(rows);
  const allValues = sendable.length > 0 && sendable.every((row) => (toNumber(row.value) ?? 0) > 0);
  return allValues && valuesModeAllowed(rows) ? 'values' : 'weights';
}

/** Why a sendable row blocks the confirm, or null. */
export function rowBlocker(row: ImportRow, mode: FigureMode): string | null {
  if (!row.inCatalog) return null;
  if (!ISIN_PATTERN.test(row.isin)) return 'ISIN non valido';
  const figure = toNumber(mode === 'weights' ? row.weight : row.value);
  if (figure === null || figure <= 0) return mode === 'weights' ? 'Inserisci il peso' : 'Inserisci il controvalore';
  return null;
}

const sum = (drafts: string[]): number =>
  drafts.reduce((total, draft) => total.add(toNumber(draft) ?? 0), new Decimal(0)).toNumber();

export type ImportTotals = {
  /** All rows: compared with the total printed in the document. */
  documentValueSum: number;
  /** Sendable rows: what the analysis receives. */
  sentValueSum: number;
  sentWeightSum: number;
};

export function computeTotals(rows: ImportRow[]): ImportTotals {
  const sendable = sendableRows(rows);
  return {
    documentValueSum: sum(rows.map((row) => row.value)),
    sentValueSum: sum(sendable.map((row) => row.value)),
    sentWeightSum: sum(sendable.map((row) => row.weight)),
  };
}

/**
 * Weights total 100 % when they show 100,00 % (the total is shown to 2 decimals): weights read with more decimals
 * and retyped as shown otherwise never add up. The manual tool needs exactly 100 %: `toManualRows` spreads the
 * rounding residue.
 */
export const weightsTotalOk = (rows: ImportRow[]): boolean =>
  new Decimal(computeTotals(rows).sentWeightSum).toDecimalPlaces(2).equals(100);

export function canConfirm(rows: ImportRow[], mode: FigureMode): boolean {
  const sendable = sendableRows(rows);
  if (sendable.length === 0) return false;
  if (sendable.some((row) => rowBlocker(row, mode) !== null)) return false;
  return mode === 'values' || weightsTotalOk(rows);
}

/**
 * Rescale the sendable weights to 100 % (2 decimals), the rounding residue on
 * the largest row. Used only at hand-over, for weights that are 100 % to the
 * shown 2 decimals (no advisor action: Robert 2026-10-19 removed it).
 */
export function rescaleWeightsTo100(rows: ImportRow[]): ImportRow[] {
  const sendable = sendableRows(rows).filter((row) => (toNumber(row.weight) ?? 0) > 0);
  const total = new Decimal(sum(sendable.map((row) => row.weight)));
  if (total.isZero()) return rows;
  const scaled = new Map(
    sendable.map((row) => [row.rowId, new Decimal(row.weight).times(100).dividedBy(total).toDecimalPlaces(2)]),
  );
  const residue = new Decimal(100).minus(Array.from(scaled.values()).reduce((a, b) => a.add(b), new Decimal(0)));
  const largest = sendable.reduce((a, b) => (scaled.get(b.rowId).greaterThan(scaled.get(a.rowId)) ? b : a));
  scaled.set(largest.rowId, scaled.get(largest.rowId).add(residue));
  return rows.map((row) => (scaled.has(row.rowId) ? { ...row, weight: scaled.get(row.rowId).toFixed() } : row));
}

/**
 * The hand-over: sendable rows as the manual table's strings, in the format its
 * own cells hold ("12.35%" by weight, "19876.4" by value), so its submit builds
 * exactly the payload it builds today.
 */
export function toManualRows(rows: ImportRow[], mode: FigureMode): ManualPortfolioRow[] {
  const exact = mode !== 'weights' || new Decimal(computeTotals(rows).sentWeightSum).equals(100);
  return sendableRows(exact ? rows : rescaleWeightsTo100(rows)).map((row) => ({
    isin: row.isin.toUpperCase(),
    weight: mode === 'weights' ? `${row.weight}%` : row.value,
  }));
}

/** The count above the table: how many rows reach the agent, how many stay out. */
export function rowCountText(sentCount: number, excludedCount: number): string {
  const sent = sentCount === 1 ? '1 strumento da analizzare' : `${sentCount} strumenti da analizzare`;
  return excludedCount === 0 ? sent : `${sent} · ${excludedCount} fuori catalogo`;
}

/** [1,2,3,5] → "1-3,5" (the endpoint's `pages` field). */
export function formatPages(pages: number[]): string {
  const sorted = Array.from(new Set(pages)).sort((a, b) => a - b);
  const ranges: string[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = sorted[i];
    while (sorted[i + 1] === sorted[i] + 1) i++;
    ranges.push(start === sorted[i] ? `${start}` : `${start}-${sorted[i]}`);
  }
  return ranges.join(',');
}

export const ERROR_MESSAGES: Record<string, string> = {
  invalid_request: 'Richiesta non valida: ricarica il documento.',
  file_too_large: 'Il documento supera la dimensione massima di 20 MB.',
  unsupported_type: 'Formato non supportato: carica un PDF, un’immagine JPEG o PNG, un file XLSX o CSV.',
  unreadable_document: 'Non è stato possibile leggere il documento. Prova con un PDF o una foto più nitida.',
  no_isin_found: 'Nessun ISIN trovato nel documento.',
  service_not_configured: 'Il caricamento dei documenti non è disponibile in questo ambiente.',
  internal: 'Elaborazione del documento non riuscita. Riprova.',
};

/** Client-side pre-flight (the api re-checks both): an error code, or null. */
export function validateDocument(file: Pick<File, 'name' | 'size'>): 'unsupported_type' | 'file_too_large' | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!PORTFOLIO_DOC_ACCEPT.split(',').includes(extension)) return 'unsupported_type';
  if (file.size > MAX_PORTFOLIO_DOC_BYTES) return 'file_too_large';
  return null;
}

/** RestService's ApiResponseError carries the body on `data`: `{error: {code, message}}`. */
export function importErrorMessage(error: unknown): string {
  const body = (error as { data?: { error?: { code?: unknown; message?: unknown } } } | null)?.data?.error;
  if (typeof body?.code === 'string' && ERROR_MESSAGES[body.code]) return ERROR_MESSAGES[body.code];
  if (typeof body?.message === 'string' && body.message.trim()) return body.message;
  return ERROR_MESSAGES.internal;
}
